import React, { useEffect, useState } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const Navbar = ({ navLinks, scrollToSection }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomeTop = (location.pathname === '/' || location.pathname === '/services/web-solutions') && !scrolled;
  const isMobileAppsTop = location.pathname === '/services/mobile-apps' && !scrolled;
  const isDesktopSoftwareTop = location.pathname === '/services/desktop-software' && !scrolled;
  const isDigitalMarketingTop = location.pathname === '/digital-marketing' && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  const handleNavClick = (link) => {
    setIsMenuOpen(false);
    if (link.isPage) {
      navigate(link.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (location.pathname !== '/') {
      navigate('/');
      window.setTimeout(() => scrollToSection(link.id, false), 80);
      return;
    }
    scrollToSection(link.id, false);
  };

  const handleBookDemo = () => {
    setIsMenuOpen(false);
    if (location.pathname === '/') {
      const demoSection = document.getElementById('demo-section');
      if (demoSection) {
        demoSection.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    navigate('/');
    window.setTimeout(() => {
      const demoSection = document.getElementById('demo-section');
      if (demoSection) {
        demoSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 120);
  };

  const navText = isHomeTop ? 'text-white/85 hover:text-white' : 'text-slate-600 hover:text-blue-700';

  return (
    <nav className={`fixed inset-x-0 top-[27px] z-50 border-b transition-all duration-300 ${isHomeTop ? 'border-white/10 bg-transparent' : isMobileAppsTop || isDesktopSoftwareTop || isDigitalMarketingTop ? 'border-slate-900/10 bg-transparent' : 'border-slate-200/80 bg-white/95 shadow-[0_6px_30px_rgba(15,23,42,.08)] backdrop-blur-xl'}`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-5 px-5 py-3 sm:px-8 lg:px-10">
        <button type="button" onClick={() => handleNavClick({ id: '/', isPage: true })} className="group flex min-w-0 items-center gap-3 text-left">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white p-1 shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-105"><img src="/logo.png" alt="GlobalEPRC logo" className="h-full w-full object-contain" /></span>
          <span className="hidden min-w-0 sm:block">
            <span className={`block truncate text-sm font-black tracking-[.02em] ${isHomeTop ? 'text-white' : 'text-slate-950'}`}>GlobalEPRC</span>
            <span className={`block max-w-[260px] truncate text-[9px] font-bold uppercase tracking-[.13em] ${isHomeTop ? 'text-amber-300' : 'text-blue-700'}`}>Ethical People Resourcing Consultancy</span>
          </span>
        </button>

        <div className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <div key={link.name} className="group relative">
              <button type="button" onClick={() => handleNavClick(link)} className={`flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-bold transition ${navText}`}>
                {link.name}{link.subLinks && <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />}
              </button>
              {link.subLinks && (
                <div className="pointer-events-none absolute left-1/2 top-full w-60 -translate-x-1/2 pt-3 opacity-0 transition duration-200 group-hover:pointer-events-auto group-hover:opacity-100">
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl">
                    {link.subLinks.map((sub) => <button type="button" key={sub.name} onClick={() => handleNavClick(sub)} className="block w-full rounded-xl px-4 py-3 text-left text-sm font-semibold text-slate-600 transition hover:bg-blue-50 hover:text-blue-800">{sub.name}</button>)}
                  </div>
                </div>
              )}
            </div>
          ))}
          <button 
            type="button" 
            onClick={handleBookDemo} 
            className="ml-3 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-slate-950 px-5 py-2.5 text-sm font-black shadow-lg shadow-amber-400/20 transition hover:-translate-y-0.5 flex items-center justify-center"
          >
            Book a Demo
          </button>
        </div>

        <button type="button" aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'} onClick={() => setIsMenuOpen((open) => !open)} className={`flex h-11 w-11 items-center justify-center rounded-xl border lg:hidden ${isHomeTop ? 'border-white/20 bg-white/10 text-white' : 'border-slate-200 bg-white text-slate-900'}`}>
          {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {isMenuOpen && (
        <div className="border-t border-slate-200 bg-white px-5 py-4 shadow-2xl lg:hidden">
          <div className="mx-auto max-w-7xl space-y-1">
            {navLinks.map((link) => <div key={link.name} className="border-b border-slate-100 py-1 last:border-0"><button type="button" onClick={() => handleNavClick(link)} className="w-full px-2 py-3 text-left text-base font-bold text-slate-900">{link.name}</button>{link.subLinks && <div className="grid gap-1 pb-2 pl-3">{link.subLinks.map((sub) => <button type="button" key={sub.name} onClick={() => handleNavClick(sub)} className="rounded-lg px-2 py-2 text-left text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-blue-700">{sub.name}</button>)}</div>}</div>)}
            <button 
              type="button" 
              onClick={handleBookDemo} 
              className="mt-3 w-full rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 px-4 py-3 font-black text-sm shadow-md flex items-center justify-center"
            >
              Book a Demo
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
